import { Stage, Layer, Line } from "react-konva";
import { useState, useRef, useEffect, useCallback } from "react";
import { socket } from "../../../socket/socketClient";

const DrawingCanvas = ({
	canvasRef,
	meetingId,
	remoteLines,
	readonly = false,
	height = 400,
}) => {
	const [lines, setLines] = useState([]);
	const [tool, setTool] = useState("pen");
	const [color, setColor] = useState("#000000");
	const [stageSize, setStageSize] = useState({ width: 300, height: 300 });
	const [userHeight, setUserHeight] = useState(height);
	const isDrawing = useRef(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (remoteLines.length) {
			setLines(remoteLines);
		}
	}, [remoteLines]);

	const updateStageSize = useCallback(() => {
		if (containerRef.current) {
			const { width } = containerRef.current.getBoundingClientRect();
			setStageSize({ width, height: userHeight });
		}
	}, [userHeight]);

	useEffect(() => {
		updateStageSize();
		window.addEventListener("resize", updateStageSize);
		return () => window.removeEventListener("resize", updateStageSize);
	}, [updateStageSize]);

	const emitLine = (newLine) => {
		if (socket.connected && meetingId) {
			socket.emit("update_meeting_field", {
				meetingId,
				field: "canvasLines",
				value: [...lines, newLine],
			});
		}
	};

	const handleMouseDown = (e) => {
		if (readonly) return;

		isDrawing.current = true;
		const pos = e.target.getStage().getPointerPosition();
		const newLine = {
			tool,
			stroke: color,
			points: [pos.x, pos.y],
			composite: tool === "eraser" ? "destination-out" : "source-over",
			strokeWidth: tool === "eraser" ? 10 : 2,
		};

		setLines((prev) => [...prev, newLine]);
	};

	const handleMouseMove = (e) => {
		if (readonly) return;

		if (!isDrawing.current) return;
		const stage = e.target.getStage();
		const point = stage.getPointerPosition();
		setLines((prev) => {
			const lastLine = prev[prev.length - 1];
			if (!lastLine) return prev;
			const updatedLine = {
				...lastLine,
				points: lastLine.points.concat([point.x, point.y]),
			};
			const updated = [...prev.slice(0, -1), updatedLine];
			return updated;
		});
	};

	const handleMouseUp = () => {
		if (readonly) return;

		isDrawing.current = false;
		const lastLine = lines[lines.length - 1];
		if (lastLine) emitLine(lastLine);
	};

	const handleHeightChange = (e) => {
		const newHeight = Number(e.target.value);
		setUserHeight(newHeight);
		setStageSize((prev) => ({ ...prev, height: newHeight }));
	};

	const renderGrid = (spacing = 20) => {
		const gridLines = [];

		for (let x = 0; x < stageSize.width; x += spacing) {
			gridLines.push(
				<Line
					key={`v-${x}`}
					points={[x, 0, x, stageSize.height]}
					stroke="#ddd"
					strokeWidth={1}
				/>
			);
		}

		for (let y = 0; y < stageSize.height; y += spacing) {
			gridLines.push(
				<Line
					key={`h-${y}`}
					points={[0, y, stageSize.width, y]}
					stroke="#ddd"
					strokeWidth={1}
				/>
			);
		}

		return gridLines;
	};

	return (
		<div
			ref={containerRef}
			className="relative space-y-4 w-full overflow-x-hidden"
		>
			<div className="flex flex-col md:flex-row space-x-4 items-center">
				{readonly === false && (
					<div className="flex flex-row space-x-4">
						<label className="flex items-center space-x-2">
							<span className="text-sm font-medium">Color:</span>
							<input
								type="color"
								value={color}
								disabled={tool === "eraser"}
								onChange={(e) => setColor(e.target.value)}
							/>
						</label>
						<button
							className={`px-3 py-1 rounded text-white ${
								tool === "pen" ? "bg-blue-600" : "bg-gray-400"
							}`}
							type="button"
							onClick={() => setTool("pen")}
						>
							{" "}
							Lápiz{" "}
						</button>
						<button
							className={`px-3 py-1 rounded text-white ${
								tool === "eraser" ? "bg-red-600" : "bg-gray-400"
							}`}
							type="button"
							onClick={() => setTool("eraser")}
						>
							{" "}
							Goma{" "}
						</button>
						<button
							className="px-3 py-1 rounded bg-yellow-600 text-white"
							type="button"
							onClick={() => setLines([])}
						>
							{" "}
							Borrar todo{" "}
						</button>
					</div>
				)}
				<div>
					<label className="flex items-center space-x-2">
						<span className="text-sm font-medium">Altura: {userHeight}px</span>
						<input
							type="range"
							min={150}
							max={window.innerHeight * 1.5}
							value={userHeight}
							onChange={handleHeightChange}
							className="cursor-pointer"
						/>
					</label>
				</div>
			</div>
			<Stage
				width={stageSize.width}
				height={stageSize.height}
				ref={canvasRef}
				onMouseDown={handleMouseDown}
				onMousemove={handleMouseMove}
				onMouseup={handleMouseUp}
				onTouchStart={handleMouseDown}
				onTouchMove={handleMouseMove}
				onTouchEnd={handleMouseUp}
				className="border-2 border-black rounded-lg"
				style={{ touchAction: "none" }}
			>
				<Layer>{renderGrid(25)}</Layer>
				<Layer>
					{lines.map((line, i) => (
						<Line
							key={i}
							points={line.points}
							stroke={line.stroke}
							strokeWidth={line.strokeWidth}
							tension={0.5}
							lineCap="round"
							globalCompositeOperation={line.composite}
						/>
					))}
				</Layer>
			</Stage>
		</div>
	);
};

export default DrawingCanvas;
